import { format } from 'date-fns'
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'react-qr-code'

import { useOrderDeliveryStop } from '@/api/deliveries/delivery-stop'
import type { Order } from '@/api/orders/orders-types'
import { ShippingUnitIcon } from '@/components/stickers/shipping-unit-icon'
import { DATE_FORMATS } from '@/constants/app'
import { cn } from '@/lib/utils'
import { formatSkidSizeWithUnit } from '@/utils/order-skids'
import {
  type PackingListLine,
  type PackingListUnit,
  buildPackingList,
  countShippingUnits,
  formatLineCounts,
  formatUnitKind,
  formatUnitPosition,
  withCivicNumber
} from '@/utils/packing-list'
import { type SheetSegment, type UnitHeights, paginateSheets } from '@/utils/packing-list-pages'

/** Paper this ticket is aimed at: the shop's 4x6 label roll or an office letter printer. */
export type PackingListPageSize = 'label' | 'letter'

export const PACKING_LIST_PAGE_SIZES: { value: PackingListPageSize; label: string }[] = [
  { value: 'label', label: '4 x 6 label' },
  { value: 'letter', label: '8.5 x 11' }
]

/** QR opens the staff phone view of this ticket, scoped to the scanned unit. */
function packingUnitUrl(order: Order, skidId: number | null) {
  const base = `${window.location.origin}/packing-list/${order.autoid}`
  return skidId != null ? `${base}?skid=${skidId}` : base
}

/**
 * 4x6 label stock, but printed like plain paper: the order header once, then units flow
 * from one label to the next wherever the page runs out. Only rows and unit headers are
 * kept from splitting across a label.
 *
 * Letter prints the same document zoomed — one layout covers both printers. The zoom is
 * set for type, not width: 1.1 puts the 10px description lines at ~8pt (the shop stepped
 * it down from 1.9 → 1.45 → 1.25), and `width: 100%` lets the column widen to fill the
 * sheet at that size. Margins are the 1/4" the shop asked for. Each order in a batch
 * starts on a fresh page.
 */
const LETTER_MARGIN_IN = 0.25
const LETTER_ZOOM = 1.1

/** Printed QR edge on letter — smaller than the label's, still a phone-scannable ~0.6in. */
const LETTER_QR_IN = 0.6

/** Printable letter width before print zoom — what the column is laid out at on paper. */
const LETTER_DOC_WIDTH = `${((8.5 - 2 * LETTER_MARGIN_IN) / LETTER_ZOOM).toFixed(2)}in`

/** Printable letter height before print zoom, in CSS px (96/in). */
const LETTER_PAGE_PX = ((11 - 2 * LETTER_MARGIN_IN) / LETTER_ZOOM) * 96

/** A sheet is drawn a hair under the page, so rounding never spills a blank one. */
const LETTER_SHEET_PX = LETTER_PAGE_PX - 2

/** Each unit block's `pb-1` + bottom rule, which the measured rows don't include. */
const UNIT_FRAME_PX = 5

/** The hairline between item rows on the sheet. */
const LINE_RULE_PX = 1

/** The rule above the first unit on every sheet. */
const UNITS_RULE_PX = 1

/** Slack for sub-pixel rounding between the measured rows and the laid-out sheet. */
const SHEET_SLACK_PX = 12

/**
 * Preview card width. On letter it adds the card's `p-3` + border back, so the card's
 * content box is the printed column: lines wrap and pages fill on screen as on paper.
 */
export const PACKING_PREVIEW_WIDTH: Record<PackingListPageSize, string> = {
  label: '4in',
  letter: `calc(${LETTER_DOC_WIDTH} + 1.5rem + 2px)`
}

/** Pre-zoom QR size, so the letter sheet prints `LETTER_QR_IN` after zoom. */
const QR_SIZE: Record<PackingListPageSize, string> = {
  label: '4rem',
  letter: `${(LETTER_QR_IN / LETTER_ZOOM).toFixed(2)}in`
}

export const packingListPageStyle = (size: PackingListPageSize) => `
  @page { size: ${size === 'letter' ? 'letter' : '4in 6in'}; margin: ${size === 'letter' ? `${LETTER_MARGIN_IN}in` : '0.15in'}; }

  @media print {
    html, body { margin: 0; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }

    /* Block flow, not flex: a column flex container fragments unreliably across pages,
       which on a tall order silently drops units instead of spilling onto the next label. */
    .packing-doc {
      display: block !important;
      width: 100% !important;
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      background: #fff !important;
      /* Letter: the 1/4" @page margin is the whole margin — the card padding would add to it. */
      ${size === 'letter' ? `padding: 0 !important; zoom: ${LETTER_ZOOM};` : ''}
    }

    .packing-order + .packing-order { break-before: page; }
    /* Batch preview draws each order as a card; on paper it is just the next sheet. */
    .packing-page {
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .packing-page + .packing-page { break-before: page; }
    /* Letter is paginated by hand — each sheet is exactly one page. */
    .packing-sheet { border: none !important; }
    .packing-sheet + .packing-sheet { break-before: page; }
    .packing-line { break-inside: avoid; }
    /* Keep a unit header glued to its first row so it never ends a label on its own. */
    .packing-unit-head { break-inside: avoid; break-after: avoid; }
    .packing-signature { break-inside: avoid; }
    .no-print { display: none !important; }
  }
`

/** Letterhead copy from the EBMS packing list this ticket replaces. */
const COMPANY = {
  address: '7806 Sixth Line, Drayton ON N0G 1P0',
  phone: '(519) 638-2746',
  fax: '(519) 638-3730',
  site: 'www.macoenterprises.ca',
  hst: 'HST Reg # 125225201'
}

/** EBMS pads its stored labels (`Net  30 days`); one space reads the same on paper. */
export const tidy = (value?: string | null) => value?.replace(/\s+/g, ' ').trim() || null

/** `Belwood, ON N0B 1J0` from the pieces EBMS sends, skipping the empty ones. */
const cityLine = (city?: string | null, state?: string | null, zip?: string | null) =>
  [[tidy(city), tidy(state)].filter(Boolean).join(', '), tidy(zip)].filter(Boolean).join(' ')

/** One cell of the EBMS info strip (Customer ID, P.O. Number). */
const InfoCell = ({ label, value }: { label: string; value?: string | null }) => (
  <div className='min-w-0'>
    <div className='text-[8px] leading-tight font-semibold uppercase'>{label}</div>
    <div className='truncate text-[10px] leading-tight'>{value || '-'}</div>
  </div>
)

const isDeliveryOrder = (order: Order) => order.ship_type?.trim().toLowerCase() === 'delivery'

const pluralUnits = (count: number) => `${count} shipping unit${count === 1 ? '' : 's'}`

/** Everything above the units: letterhead, ship-to, customer, info strip, unit count. */
const OrderHeader = ({
  order,
  isLetter,
  shippingUnits,
  routificAddress
}: {
  order: Order
  isLetter: boolean
  shippingUnits: number
  /** Routific's cleaned-up address for this delivery, looked up once per order by the sheets. */
  routificAddress?: string | null
}) => {
  const customerAddress = tidy(order.address)
  const customerCity = cityLine(order.city, order.state, order.zip)
  // Only a delivery has somewhere to ship to; a pickup leaves the block off. Routific's
  // cleaned-up address wins over the raw one EBMS pushed, when Routific has the stop.
  const isDelivery = isDeliveryOrder(order)
  // The name only when it isn't the customer's, which is printed right beside it already.
  const shipName = tidy(order.ship_to)
  const shipToName = shipName && shipName.toLowerCase() !== tidy(order.customer)?.toLowerCase() ? shipName : null
  const shipAddress = routificAddress ?? tidy(order.ship_address)
  const shipCity = routificAddress ? null : cityLine(order.ship_city, order.ship_state, order.ship_zip)
  const hasShipTo = isDelivery && !!(shipAddress || shipCity)

  return (
    <div className='space-y-2'>
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0'>
          {/* Letter: colour, and flush with the text — the -ml-1 nudge reached into the
              1/4" margin, where office printers clip. The 4x6 thermal label stays as it was. */}
          {/* width/height give the box its aspect before the SVG loads — the letter sheets
              measure the header, and a logo still loading would measure as 0px tall. */}
          <img
            className={cn('h-auto', isLetter ? 'w-40' : '-ml-1 w-32 grayscale')}
            src='/maco-50-logo.svg'
            alt='Maco enterprises inc'
            width={2792}
            height={1008}
          />
          <div className='mt-1 text-[9px] leading-tight'>{COMPANY.address}</div>
          <div className='text-[9px] leading-tight'>
            Phone: {COMPANY.phone} · Fax: {COMPANY.fax}
          </div>
          {isLetter ? <div className='text-[9px] leading-tight'>{COMPANY.site}</div> : null}
        </div>
        <div className='shrink-0 text-right'>
          <div className='text-base leading-none font-bold'>Packing List</div>
          <div className='mt-1 text-lg leading-none font-bold'>#{order.invoice}</div>
          <div className='text-[10px] leading-tight'>
            {order.ship_date ? format(order.ship_date, DATE_FORMATS.dots) : '-'} {order.ship_time ?? ''}
          </div>
          {/* On letter, ship via sits with the ship-to under the letterhead rule. */}
          {isLetter ? null : <div className='text-[10px] leading-tight'>Ship via: {order.ship_type || '-'}</div>}
        </div>
      </div>

      {isLetter ? (
        <>
          {/* Customer (with its address), tag, then ship via with the ship-to address under it. */}
          <div className='flex gap-4 border-t border-[#000] pt-1.5'>
            <div className='min-w-0 flex-1'>
              <div className='text-base leading-tight font-semibold'>{order.customer}</div>
              {customerAddress ? <div className='text-[10px] leading-tight'>{customerAddress}</div> : null}
              {customerCity ? <div className='text-[10px] leading-tight'>{customerCity}</div> : null}
            </div>
            <div className='min-w-0 flex-1 text-xs leading-tight'>
              <div className='text-[8px] font-semibold uppercase'>Tag</div>
              <div className='text-base leading-tight font-semibold'>{order.ship_tag || '-'}</div>
            </div>
            {/* Label beside its value, at the item description's size; the address's later
                lines align under its first. */}
            <div className='grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)] content-start gap-x-1.5 text-[10px] leading-tight'>
              <span className='font-semibold'>Ship via:</span>
              <span>{order.ship_type || '-'}</span>
              {hasShipTo ? (
                <>
                  <span className='font-semibold'>Ship to:</span>
                  <span className='min-w-0 break-words'>
                    {shipToName ? <span className='block font-semibold'>{shipToName}</span> : null}
                    {shipAddress ? <span className='block'>{shipAddress}</span> : null}
                    {shipCity ? <span className='block'>{shipCity}</span> : null}
                  </span>
                </>
              ) : null}
            </div>
          </div>

          {/* Sales Person and Terms dropped at the shop's request. */}
          <div className='grid grid-cols-4 gap-2 border-y border-[#000] py-1.5'>
            <InfoCell
              label='Customer ID'
              value={order.customer_id}
            />
            <InfoCell
              label='P.O. Number'
              value={order.po_no}
            />
          </div>

          {/* Same 8px above (the stack gap) and below, so it sits centred between the rules. */}
          <div className='pb-2 text-xs leading-tight'>{pluralUnits(shippingUnits)}</div>
        </>
      ) : (
        <div className='border-y border-[#000] py-1.5'>
          <div className='text-base leading-tight font-semibold'>{order.customer}</div>
          <div className='text-xs leading-tight'>Tag: {order.ship_tag || '-'}</div>
          {hasShipTo ? (
            <div className='text-xs leading-tight'>
              Ship to: {[shipToName, shipAddress, shipCity].filter(Boolean).join(', ')}
            </div>
          ) : null}
          <div className='text-xs leading-tight'>{pluralUnits(shippingUnits)}</div>
        </div>
      )}
    </div>
  )
}

/** Unit code, icon, label, count line and QR. `continued` marks a repeat on a later sheet. */
const UnitHead = ({
  order,
  unit,
  units,
  pageSize,
  continued,
  qrPlaceholder
}: {
  order: Order
  unit: PackingListUnit
  /** The order's whole list, for the unit's position in it. */
  units: PackingListUnit[]
  pageSize: PackingListPageSize
  continued?: boolean
  /** Same-size empty box — the hidden measuring copy doesn't need a real QR. */
  qrPlaceholder?: boolean
}) => {
  const qrStyle = { width: QR_SIZE[pageSize], height: QR_SIZE[pageSize] }
  const size = formatSkidSizeWithUnit(unit.size)
  const pieces = unit.lines.reduce((sum, line) => sum + line.quantity, 0)
  const title = formatUnitKind(unit) ?? (unit.skidId == null ? 'Not packed yet' : 'Unit')

  return (
    <div className='packing-unit-head flex items-center justify-between gap-2 py-1.5'>
      <div className='min-w-0'>
        {/* What the unit is, how big and how heavy leads — that is what a loader or a
            customer recognises. The system code (`LHB-0923-6316`) is only a reference. */}
        <div className='flex items-center gap-1.5 text-base leading-none font-bold'>
          {unit.kind ? (
            <ShippingUnitIcon
              kind={unit.kind}
              className='size-5 shrink-0'
            />
          ) : null}
          <span>{title}</span>
          {size ? <span>{size}</span> : null}
          {/* Whole pounds — the shop has no use for the decimal on paper. */}
          {unit.weight != null ? <span>· {Math.round(unit.weight)} lb</span> : null}
          {/* Boxed on the title line: a split unit must not read as a new one. An outline, not
              a border, so the head keeps the height it was measured at without it. */}
          {continued ? <span className='ml-1 px-1 uppercase tracking-wide outline-2 outline-offset-1 outline-[#000]'>Continued</span> : null}
        </div>
        <div className='mt-1 text-[10px] leading-tight'>
          {unit.label ? <span className='font-mono'>{unit.label} · </span> : null}
          {formatUnitPosition(units, unit)}
          {` · ${pieces} pcs`}
        </div>
      </div>
      {qrPlaceholder ? (
        <div
          className='shrink-0'
          style={qrStyle}
        />
      ) : (
        <QRCode
          className='shrink-0'
          style={qrStyle}
          size={256}
          value={packingUnitUrl(order, unit.skidIds.length > 1 ? null : unit.skidId)}
          viewBox='0 0 256 256'
        />
      )}
    </div>
  )
}

const LineRow = ({ line, isLetter }: { line: PackingListLine; isLetter: boolean }) => (
  <div className={cn('packing-line flex gap-2 py-1', isLetter ? 'items-baseline' : 'items-start')}>
    <span className='w-10 shrink-0 text-right text-sm leading-tight font-semibold tabular-nums'>
      {line.quantity}
      {line.uom ? <span className='ml-0.5 text-[9px] font-normal'>{line.uom}</span> : null}
    </span>
    {isLetter ? (
      // Letter: one line per item — qty, product ID, description, colour, counts.
      // The 4x6 column is too narrow, so it keeps the stacked form below.
      <div className='flex min-w-0 flex-1 items-baseline gap-3'>
        <span className='w-20 shrink-0 text-[11px] leading-tight font-semibold break-words'>
          {line.inven}
        </span>
        <span className='min-w-0 flex-1 text-[10px] leading-tight'>{line.descr}</span>
        {/* Fixed-width colour and counts, so every row's columns line up. */}
        <span className='w-36 shrink-0 text-[9px] leading-tight font-semibold break-words uppercase'>
          {line.color ?? '-'}
        </span>
        <span className='w-44 shrink-0 text-right text-[9px] leading-tight whitespace-nowrap tabular-nums'>
          {formatLineCounts(line)}
        </span>
      </div>
    ) : (
      <div className='min-w-0 flex-1'>
        <div className='text-[11px] leading-tight font-semibold'>
          {line.inven}
          {line.color ? <span className='ml-1.5 text-[9px] uppercase'>{line.color}</span> : null}
        </div>
        <div className='text-[10px] leading-tight'>{line.descr}</div>
        <div className='text-[9px] leading-tight tabular-nums'>{formatLineCounts(line)}</div>
      </div>
    )}
  </div>
)

/** A shipping unit as one block — header and its items inside the same rules. */
const UnitBlock = ({ className, children }: { className?: string; children: React.ReactNode }) => (
  <div className={cn('packing-unit border-b border-[#000] pb-1', className)}>{children}</div>
)

const SignatureLines = () => (
  <div className='flex items-end gap-3'>
    <div className='min-w-0 flex-1'>
      <div className='h-5 border-b border-[#000]' />
      <div className='mt-0.5 text-[9px] leading-tight'>Customer signature</div>
    </div>
    <div className='w-20 shrink-0'>
      <div className='h-5 border-b border-[#000]' />
      <div className='mt-0.5 text-[9px] leading-tight'>Date</div>
    </div>
  </div>
)

const FooterRow = ({ page }: { page?: string }) => (
  <div className='mt-1.5 flex items-end justify-between gap-2 text-[9px] leading-tight'>
    <span>{COMPANY.hst}</span>
    {page ? <span className='font-semibold tabular-nums'>Page {page}</span> : null}
    <span className='italic'>This is only a Packing List</span>
  </div>
)

const EmptyUnits = () => (
  <div className='py-6 text-center text-sm'>No bundles on this order yet — nothing to pack.</div>
)

interface PackingListOrderProps {
  order: Order
  pageSize?: PackingListPageSize
}

/**
 * One order's ticket: the EBMS packing list's letterhead and footer around our own
 * by-shipping-unit body. Letter gets the office blocks (site, ship-to, customer address,
 * tag, info strip), one-line items and real sheets; the 4x6 label keeps its compact
 * stacked form and prints as one continuous ticket.
 */
export const PackingListOrder = ({ order, pageSize = 'label' }: PackingListOrderProps) => {
  const units = useMemo(() => buildPackingList(order), [order])
  const shippingUnits = countShippingUnits(units)
  // Once per order, for both sizes: Routific's cleaned-up ship-to beats the raw EBMS one,
  // which on some orders is just the customer's own address.
  const { data: stop } = useOrderDeliveryStop(
    isDeliveryOrder(order) ? order.autoid : undefined,
    order.ship_date?.slice(0, 10)
  )
  const routificAddress = withCivicNumber(tidy(stop?.address), order.ship_address)

  if (pageSize === 'letter') {
    return (
      <LetterSheets
        order={order}
        units={units}
        shippingUnits={shippingUnits}
        routificAddress={routificAddress}
      />
    )
  }

  return (
    <div className='packing-order space-y-2'>
      <OrderHeader
        order={order}
        isLetter={false}
        shippingUnits={shippingUnits}
        routificAddress={routificAddress}
      />

      {units.length === 0 ? (
        <EmptyUnits />
      ) : (
        // The customer block above and the signature below already draw the outer rules.
        <div>
          {units.map((unit) => (
            <UnitBlock
              key={unit.skidId ?? 'unpacked'}
              className='last:border-b-0'
            >
              <UnitHead
                order={order}
                unit={unit}
                units={units}
                pageSize='label'
              />
              <div className='divide-y divide-[#00000022]'>
                {unit.lines.map((line) => (
                  <LineRow
                    key={line.key}
                    line={line}
                    isLetter={false}
                  />
                ))}
              </div>
            </UnitBlock>
          ))}
        </div>
      )}

      {/* Same closing block as the EBMS sheet: signature, tax number, the disclaimer. */}
      <div className='packing-signature border-t border-[#000] pt-2'>
        <SignatureLines />
        <FooterRow />
      </div>
    </div>
  )
}

/** Everything measured except `source`, for a cheap "did anything move" check. */
const sizesKey = (h: SheetHeights) => JSON.stringify([h.header, h.footer, h.signature, h.units])

interface SheetHeights {
  /** The units these were measured from — stale once the order refetches. */
  source: PackingListUnit[]
  header: number
  footer: number
  signature: number
  units: UnitHeights[]
}

/**
 * Letter, paginated by hand: every sheet repeats the order header and carries `Page x / N`,
 * the signature sits at the foot of the last one. Rows are measured in a hidden copy laid
 * out at the printed column width, then packed into sheets — the browser's own page breaks
 * can't repeat a header or number pages per order in a batch.
 */
const LetterSheets = ({
  order,
  units,
  shippingUnits,
  routificAddress
}: {
  order: Order
  units: PackingListUnit[]
  shippingUnits: number
  /** Looked up once per order by the caller. A late answer resizes the header, and the
   * measuring copy re-paginates from there. */
  routificAddress: string | null
}) => {
  const measureRef = useRef<HTMLDivElement>(null)
  const [heights, setHeights] = useState<SheetHeights | null>(null)

  useLayoutEffect(() => {
    const root = measureRef.current
    if (!root) return
    const height = (selector: string) => root.querySelector<HTMLElement>(selector)?.offsetHeight ?? 0
    const measure = () => {
      const next: SheetHeights = {
        source: units,
        header: height('[data-m="header"]'),
        footer: height('[data-m="footer"]'),
        signature: height('[data-m="signature"]'),
        units: units.map((unit, u) => ({
          head: height(`[data-m="head-${u}"]`),
          // +1 for the `divide-y` rule each row gets on the sheet but not in isolation.
          lines: unit.lines.map((_, l) => height(`[data-m="line-${u}-${l}"]`) + LINE_RULE_PX)
        }))
      }
      setHeights((prev) => (prev?.source === next.source && sizesKey(prev) === sizesKey(next) ? prev : next))
    }
    measure()
    // The logo and fonts land after the first layout and change the header height.
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    return () => observer.disconnect()
  }, [units])

  // Heights from an earlier render of the order point at units or lines that may be gone;
  // until the layout effect re-measures, lay everything out on one sheet.
  const measured = heights?.source === units ? heights : null

  const sheets = useMemo<SheetSegment[][]>(() => {
    const heights = measured
    if (!heights)
      return [
        units.map((unit, unitIndex) => ({ unitIndex, lines: unit.lines.map((_, l) => l), continued: false }))
      ]
    return paginateSheets({
      units: heights.units,
      capacity: LETTER_SHEET_PX - heights.header - heights.footer - UNITS_RULE_PX - SHEET_SLACK_PX,
      framePx: UNIT_FRAME_PX,
      signature: heights.signature
    })
  }, [measured, units])

  const footerPx = (isLast: boolean) =>
    measured ? measured.footer + (isLast ? measured.signature : 0) : undefined

  const head = (unitIndex: number, continued = false, measure = false) => (
    <UnitHead
      order={order}
      unit={units[unitIndex]}
      units={units}
      pageSize='letter'
      continued={continued}
      qrPlaceholder={measure}
    />
  )

  return (
    <div className='packing-order relative'>
      {/* Hidden copy for measuring, at the same width as the sheets; never printed. */}
      <div
        ref={measureRef}
        aria-hidden
        className='no-print pointer-events-none invisible absolute inset-x-0 top-0'
      >
        <div data-m='header'>
          <OrderHeader
            order={order}
            isLetter
            shippingUnits={shippingUnits}
            routificAddress={routificAddress}
          />
        </div>
        {units.map((unit, u) => (
          <Fragment key={unit.skidId ?? 'unpacked'}>
            <div data-m={`head-${u}`}>{head(u, false, true)}</div>
            {unit.lines.map((line, l) => (
              <div
                key={line.key}
                data-m={`line-${u}-${l}`}
              >
                <LineRow
                  line={line}
                  isLetter
                />
              </div>
            ))}
          </Fragment>
        ))}
        <div
          data-m='signature'
          className='pt-2'
        >
          <SignatureLines />
        </div>
        <div data-m='footer'>
          <FooterRow page='1 / 1' />
        </div>
      </div>

      {sheets.map((segments, sheet) => {
        const isLast = sheet === sheets.length - 1
        return (
          <div
            key={sheet}
            className={cn('packing-sheet relative', !isLast && 'border-grey-400 border-b border-dashed')}
            // Min, not fixed: if a measurement is off, the sheet runs onto an extra page
            // instead of clipping an item off the paper. The padding keeps rows clear of
            // the pinned footer.
            style={{ minHeight: LETTER_SHEET_PX, paddingBottom: footerPx(isLast) }}
          >
            <OrderHeader
              order={order}
              isLetter
              shippingUnits={shippingUnits}
              routificAddress={routificAddress}
            />

            {units.length === 0 ? (
              <EmptyUnits />
            ) : segments.length ? (
              <div className='border-t border-[#000]'>
                {segments.map((segment) => (
                  <UnitBlock key={`${segment.unitIndex}-${segment.lines[0] ?? 'head'}`}>
                    {head(segment.unitIndex, segment.continued)}
                    <div className='divide-y divide-[#00000022]'>
                      {segment.lines.map((l) => (
                        <LineRow
                          key={units[segment.unitIndex].lines[l].key}
                          line={units[segment.unitIndex].lines[l]}
                          isLetter
                        />
                      ))}
                    </div>
                  </UnitBlock>
                ))}
              </div>
            ) : null}

            <div className='absolute inset-x-0 bottom-0'>
              {isLast ? (
                <div className='packing-signature pt-2'>
                  <SignatureLines />
                </div>
              ) : null}
              <FooterRow page={`${sheet + 1} / ${sheets.length}`} />
            </div>
          </div>
        )
      })}
    </div>
  )
}
